import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { formatUnits } from 'viem';
import { useConnection, useReadContract } from 'wagmi';

import ArrowDownSwapIcon from '@/app/assets/svg/arrow-down-swap.svg?react';
import ChevronLeftIcon from '@/app/assets/svg/chevron-left.svg?react';
import RefreshIcon from '@/app/assets/svg/refresh.svg?react';
import UsdcIcon from '@/app/assets/token/UsdcIcon';
import {
  BUY_TOKEN,
  DEFAULT_SLIPPAGE,
  PRICE_IMPACT_HIGH,
  PRICE_IMPACT_LOW,
  PRICE_IMPACT_TOO_LARGE,
  SLIPPAGE_HIGH,
  SLIPPAGE_MIN,
  SLIPPAGE_PRESETS,
} from '@/app/components/LlamaSwap/constants';
import { SwapAlert } from '@/app/components/LlamaSwap/SwapAlert';
import { isValidSlippageForQuote } from '@/app/components/LlamaSwap/useSwapQuote';
import { DepositStepperFlow } from '@/app/components/Modal/depositStepper';
import {
  getDepositStepperAlert,
  getDepositStepperPrimary,
  isDepositStepperComplete,
  mapDepositStepperStages,
} from '@/app/components/Modal/mapDepositStepperStages';
import { SequentialStepView } from '@/app/components/Modal/SequentialStepModal';
import { StepStatus } from '@/app/components/Modal/StepIcon';
import { useUsdcSwapDeposit } from '@/app/components/VaultsItemPageComponents/hooks/useUsdcSwapDeposit';
import VaultAvatar from '@/app/components/VaultsPageComponents/VaultAvatar';
import { MZUSD_DECIMALS, TRANCHE_SHARE_DECIMALS } from '@/app/shared/constants/common';
import {
  VAULT_TRANCHE_LABEL,
  VAULT_TRANCHE_TITLE,
  VaultTrancheId,
} from '@/app/shared/constants/vaults';
import { getTrancheAddressById } from '@/app/shared/constants/web3/chainContracts';
import { ABI } from '@/app/shared/constants/web3/constants';
import { useChainContracts } from '@/app/shared/hooks/blockchain/useChainContracts';
import { cn } from '@/app/shared/lib/cn';
import { formatNumber, formatNumberInputValue } from '@/app/shared/lib/formatters';
import { preloadModals } from '@/app/shared/lib/preloadModals';
import { safeParseUnits } from '@/app/shared/lib/safeParseUnits';
import { Button } from '@/app/shared/ui/Button';
import { Tabs } from '@/app/shared/ui/Tabs';

const ASSET_TO_MZUSD_SCALE = 10n ** BigInt(MZUSD_DECIMALS - BUY_TOKEN.decimals);

const DepositStepperModal = lazy(() => import('@/app/components/Modal/DepositStepperModal'));

const PREVIEW_STEPS: SequentialStepView[] = [
  {
    id: 'approve-swap',
    sectionId: 'swap',
    title: 'Accept Spending for Swap',
    description: 'Approve USDC spending for the swap contract',
    status: StepStatus.Pending,
  },
  {
    id: 'swap-usdc',
    sectionId: 'swap',
    title: 'Swap Transaction',
    description: 'Swap USDC into wYLDS',
    status: StepStatus.Pending,
  },
  {
    id: 'approve-wylds',
    sectionId: 'deposit',
    title: 'Accept Spending to mint mzUSD',
    description: 'Approve wYLDS spending for the MZUSD contract',
    status: StepStatus.Pending,
  },
  {
    id: 'mint-mzusd',
    sectionId: 'deposit',
    title: 'Mint mzUSD',
    description: 'Deposit wYLDS to receive mzUSD',
    status: StepStatus.Pending,
  },
  {
    id: 'approve-mzusd',
    sectionId: 'deposit',
    title: 'Accept Spending to Deposit',
    description: 'Approve MZUSD spending for the tranche contract',
    status: StepStatus.Pending,
  },
  {
    id: 'deposit-tranche',
    sectionId: 'deposit',
    title: 'Deposit to Tranche',
    description: 'Deposit MZUSD to receive tranche shares',
    status: StepStatus.Pending,
  },
];

const QUOTE_REFRESH_SEC = 15;

type DepositSwapDetailsFormProps = {
  usdcAmount: string;
  trancheId: VaultTrancheId;
  vaultId?: string;
  vaultName?: string;
  assetSymbol?: string;
  trancheSymbol?: string;
  onBack: () => void;
  onFlowComplete?: (wyldsAmount: string) => void;
};

const TokenAmountRow = ({
  icon,
  symbol,
  amount,
  usdLabel,
  symbolMinCh,
}: {
  icon: React.ReactNode;
  symbol: string;
  amount: string;
  usdLabel: string;
  symbolMinCh: number;
}) => (
  <div className='flex items-center gap-3 rounded-md border border-gray-700 p-5 [:root.light_&]:border-gray-900'>
    <div className='flex min-w-0 flex-1 items-center gap-2.5'>
      <div className='flex shrink-0 items-center gap-2.5'>
        {icon}
        <span
          className='text-base font-medium text-gray-200'
          style={{ minWidth: `${symbolMinCh}ch` }}
        >
          {symbol}
        </span>
      </div>
      <span className='block min-w-0 truncate text-left text-[20px] leading-7 text-gray-500'>
        {amount || '0.00'}
      </span>
    </div>
    <span className='shrink-0 text-base leading-6 text-gray-500'>{usdLabel}</span>
  </div>
);

const DepositSwapDetailsForm = ({
  usdcAmount,
  trancheId,
  vaultId,
  vaultName,
  assetSymbol = 'wYLDS',
  trancheSymbol,
  onBack,
  onFlowComplete,
}: DepositSwapDetailsFormProps) => {
  const [activeSlippageTab, setActiveSlippageTab] = useState<string>(DEFAULT_SLIPPAGE);
  const [customSlippage, setCustomSlippage] = useState('');
  const [slippage, setSlippage] = useState(DEFAULT_SLIPPAGE);
  const [secondsLeft, setSecondsLeft] = useState(QUOTE_REFRESH_SEC);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isConnected } = useConnection();

  const {
    buyAmount,
    rate,
    minReceived,
    priceImpact,
    sellUsd,
    buyUsd,
    providerLabel,
    isLoadingQuote,
    isQuoteFetching,
    dataUpdatedAt,
    showQuoteError,
    activeQuote,
    completedWyldsAmount,
    steps,
    handleSend,
    resetTransactionState,
    errorMessage,
    isUserRejectedError,
    isRunning,
    isComplete,
    isSequentialReady,
    supportsBatching,
    swapSkipped,
  } = useUsdcSwapDeposit({
    usdcAmount,
    slippage,
    trancheId,
    vaultId,
    trancheSymbol,
  });

  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useEffect(() => {
    if (isConnected) return;
    setIsModalOpen(false);
    onBackRef.current();
  }, [isConnected]);

  const contracts = useChainContracts(vaultId);
  const trancheAddress = contracts ? getTrancheAddressById(contracts, trancheId) : undefined;

  const buyAmountWylds = useMemo(
    () => safeParseUnits(buyAmount || '0', BUY_TOKEN.decimals) ?? 0n,
    [buyAmount],
  );
  const expectedWyldsRaw = useMemo(() => {
    if (!activeQuote?.amountReturned) return '';
    try {
      return formatUnits(BigInt(activeQuote.amountReturned), BUY_TOKEN.decimals);
    } catch {
      return '';
    }
  }, [activeQuote?.amountReturned]);
  const assetsForShares =
    buyAmountWylds > 0n ? buyAmountWylds * ASSET_TO_MZUSD_SCALE : 0n;

  const { data: trancheShares } = useReadContract({
    address: trancheAddress,
    abi: ABI.TRANCHE,
    functionName: 'convertToShares',
    args: [assetsForShares],
    query: {
      enabled: !!trancheAddress && assetsForShares > 0n,
    },
  });

  const hasQuote = !!activeQuote;
  const trancheSharesLabel =
    hasQuote && trancheShares !== undefined && assetsForShares > 0n
      ? formatNumber(formatUnits(trancheShares, TRANCHE_SHARE_DECIMALS), 6, 3)
      : '-';

  const swapStepperFlow = supportsBatching
    ? DepositStepperFlow.SwapBatch
    : DepositStepperFlow.SwapNoBatch;
  const stepperSteps = steps.length > 0 ? steps : PREVIEW_STEPS;
  const trancheLabel = VAULT_TRANCHE_TITLE[trancheId];
  const stepperErrorOptions = useMemo(
    () =>
      errorMessage
        ? {
            errorMessage,
            isUserRejectedError,
          }
        : undefined,
    [errorMessage, isUserRejectedError],
  );
  const stepperStages = useMemo(
    () =>
      mapDepositStepperStages(stepperSteps, {
        trancheLabel,
        flow: swapStepperFlow,
        swapSkipped,
        ...stepperErrorOptions,
      }),
    [stepperSteps, trancheLabel, swapStepperFlow, swapSkipped, stepperErrorOptions],
  );
  const isStepperSuccess = isComplete || isDepositStepperComplete(stepperStages);
  const primaryAction = useMemo(
    () =>
      getDepositStepperPrimary(stepperStages, {
        trancheLabel,
        flow: swapStepperFlow,
        forceComplete: isStepperSuccess,
      }),
    [isStepperSuccess, stepperStages, trancheLabel, swapStepperFlow],
  );
  const stepperAlert = useMemo(
    () =>
      getDepositStepperAlert(stepperStages, {
        trancheLabel,
        flow: swapStepperFlow,
        forceComplete: isStepperSuccess,
        ...stepperErrorOptions,
      }),
    [isStepperSuccess, stepperStages, trancheLabel, swapStepperFlow, stepperErrorOptions],
  );

  useEffect(() => {
    setSecondsLeft(QUOTE_REFRESH_SEC);
    const id = window.setInterval(() => {
      setSecondsLeft((prev) => (prev <= 1 ? QUOTE_REFRESH_SEC : prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [usdcAmount, slippage, dataUpdatedAt]);

  const handleStepperPrimaryClick = () => {
    if (isStepperSuccess) {
      const amount = completedWyldsAmount || buyAmount;
      setIsModalOpen(false);
      resetTransactionState();
      onFlowComplete?.(amount);
      return;
    }
    if (isSequentialReady) {
      void handleSend();
    }
  };

  const handleSlippageTabChange = (id: string | number) => {
    if (isRunning) return;
    const next = String(id);
    setActiveSlippageTab(next);
    if (next !== 'custom') {
      setSlippage(next);
      setCustomSlippage('');
    } else {
      setSlippage('');
      setCustomSlippage('');
    }
  };

  const handleCustomSlippageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isRunning) return;
    const value = formatNumberInputValue(event.target.value, customSlippage);
    const num = Number(value);
    if (value === '' || (num >= 0 && num <= 100)) {
      setCustomSlippage(value);
      setSlippage(value);
    }
  };

  const handleCustomSlippageBlur = () => {
    if (!customSlippage || Number(customSlippage) === 0) {
      setCustomSlippage('');
      setActiveSlippageTab(DEFAULT_SLIPPAGE);
      setSlippage(DEFAULT_SLIPPAGE);
    }
  };

  const onActionClick = () => {
    if (!activeQuote || isRunning) return;
    if (!slippage || Number(slippage) === 0) return;
    if (!minReceived || Number(minReceived) <= 0) return;
    preloadModals();
    setIsModalOpen(true);
  };

  const sellUsdLabel = sellUsd != null ? `$ ${formatNumber(sellUsd, 2, 2)}` : '-';
  const buyUsdLabel = hasQuote && buyUsd != null ? `$ ${formatNumber(buyUsd, 2, 2)}` : '-';
  const rateLabel = hasQuote && rate ? `1 USDC = ${rate} ${assetSymbol}` : '-';
  const expectedOutputLabel =
    hasQuote && expectedWyldsRaw ? `${expectedWyldsRaw} ${assetSymbol}` : '-';
  const priceImpactLabel = hasQuote && priceImpact != null ? `${priceImpact}%` : '-';
  const minReceivedLabel =
    hasQuote && minReceived ? `${formatNumber(minReceived, 6, 2)} ${assetSymbol}` : '-';

  const isSlippageValid = isValidSlippageForQuote(slippage);
  const isZeroSlippage = slippage === '0' || slippage === '0.';
  const slippageNum = Number(slippage);
  const isSlippageHigh = isSlippageValid && slippageNum >= SLIPPAGE_HIGH;
  const isSlippageBelowMin =
    isSlippageValid && !isSlippageHigh && slippageNum > 0 && slippageNum < SLIPPAGE_MIN;
  const isSlippageAtMin = isSlippageValid && !isSlippageHigh && slippageNum === SLIPPAGE_MIN;
  const isSlippageTooLow = isSlippageBelowMin || isSlippageAtMin;

  const priceImpactNum = priceImpact != null ? Number(priceImpact) : null;
  const isPILow =
    priceImpactNum != null && priceImpactNum >= PRICE_IMPACT_LOW && priceImpactNum < PRICE_IMPACT_HIGH;
  const isPIHigh =
    priceImpactNum != null && priceImpactNum >= PRICE_IMPACT_HIGH && priceImpactNum < PRICE_IMPACT_TOO_LARGE;
  const isPITooLarge = priceImpactNum != null && priceImpactNum >= PRICE_IMPACT_TOO_LARGE;

  const customSlippageValueColor =
    isSlippageHigh || isZeroSlippage ? 'text-feedback-warning' : 'text-brand-1100';

  const priceImpactColor =
    isPIHigh || isPITooLarge ? 'text-feedback-error' : 'text-modal-result-body';

  const footerAlert = (() => {
    if (isPITooLarge) return { message: 'Price impact is too large', variant: 'warning' as const };
    if (isPIHigh) return { message: 'High Price Impact', variant: 'warning' as const };
    if (isSlippageHigh) return { message: 'Slippage is too high', variant: 'warning' as const };
    if (isSlippageTooLow) {
      return {
        message: 'Slippage is too low — transaction may fail.',
        variant: 'warning' as const,
      };
    }
    if (isPILow) return { message: 'Low Price Impact', variant: 'warning' as const };
    return null;
  })();

  const isMinReceivedZero = !!minReceived && Number(minReceived) <= 0;

  const isPriceImpactUnavailable =
    !!activeQuote &&
    !isLoadingQuote &&
    !showQuoteError &&
    !!buyAmount &&
    priceImpact == null;

  const isActionDisabled =
    isRunning ||
    !isSlippageValid ||
    !activeQuote ||
    showQuoteError ||
    isPriceImpactUnavailable ||
    isPITooLarge ||
    isMinReceivedZero;

  const isDangerButton =
    showQuoteError || isPriceImpactUnavailable || isPIHigh || isPITooLarge;

  const outputSymbol = trancheSymbol ?? VAULT_TRANCHE_LABEL[trancheId];
  const symbolMinCh = Math.max('USDC'.length, outputSymbol.length);

  return (
    <>
      <div className='flex h-full w-full min-w-0 flex-col gap-5'>
        <button
          type='button'
          onClick={onBack}
          disabled={isRunning}
          className='flex w-fit items-center gap-1 text-base text-gray-400 transition-colors hover:text-gray-200 disabled:opacity-50'
        >
          <ChevronLeftIcon className='h-3.5 w-3.5' aria-hidden />
          Back
        </button>

        <div className='relative flex flex-col gap-2'>
          <TokenAmountRow
            icon={<UsdcIcon className='h-8 w-8 shrink-0 rounded-full' aria-hidden />}
            symbol='USDC'
            amount={usdcAmount.trim() || '0.00'}
            usdLabel={sellUsdLabel}
            symbolMinCh={symbolMinCh}
          />

          <div className='pointer-events-none absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2'>
            <span className='flex h-8.5 w-8.5 items-center justify-center rounded-md border border-gray-700 bg-elevated-card-2 p-2 text-brand-1100 [:root.light_&]:border-decoration-stroke [:root.light_&]:bg-gray-800'>
              <ArrowDownSwapIcon className='h-full w-auto' aria-hidden />
            </span>
          </div>

          <TokenAmountRow
            icon={<VaultAvatar vaultId={vaultId} className='h-8 w-8' />}
            symbol={outputSymbol}
            amount={trancheSharesLabel}
            usdLabel={buyUsdLabel}
            symbolMinCh={symbolMinCh}
          />
        </div>

        <div className='flex flex-col gap-3 border-b border-decoration-stroke pb-5 [:root.light_&]:border-gray-900 sm:flex-row sm:items-center sm:justify-between sm:gap-4'>
          <span className='shrink-0 text-base text-brand-1100'>Slippage</span>
          <Tabs
            tabs={[
              ...SLIPPAGE_PRESETS.map(({ id, label }) => ({
                id,
                content: label,
              })),
              {
                id: 'custom',
                content: (
                  <div className='flex w-full max-w-17.5 items-center justify-center gap-0.5'>
                    {activeSlippageTab === 'custom' ? (
                      <>
                        <input
                          type='text'
                          value={customSlippage}
                          onChange={handleCustomSlippageChange}
                          onBlur={handleCustomSlippageBlur}
                          onClick={(event) => event.stopPropagation()}
                          placeholder='0-100'
                          className={cn(
                            'w-full min-w-0 bg-transparent text-center outline-none placeholder:text-gray-500',
                            customSlippageValueColor,
                          )}
                          maxLength={5}
                          autoFocus
                          disabled={isRunning}
                        />
                        <span className='shrink-0 text-gray-400'>%</span>
                      </>
                    ) : (
                      'Custom'
                    )}
                  </div>
                ),
              },
            ]}
            activeTab={activeSlippageTab}
            onTabChange={handleSlippageTabChange}
            className={cn(
              'flex h-9.5 w-full max-w-full shrink-0 overflow-hidden rounded-md bg-background p-0.5 sm:w-auto',
              isRunning && 'pointer-events-none opacity-50',
            )}
            tabClassName='flex h-full min-w-0 flex-1 items-center justify-center overflow-hidden rounded-sm px-2 text-sm leading-5 text-gray-200 sm:w-18 sm:flex-none'
            activeTabClassName='bg-elevated-card-2 text-gray-200'
            inactiveTabClassName='bg-transparent text-gray-400'
          />
        </div>

        <div className='flex flex-col gap-4.5'>
          <div className='flex flex-col items-start gap-1 text-base leading-6 min-[600px]:flex-row min-[600px]:items-center min-[600px]:justify-between min-[600px]:gap-1.5'>
            <span className='shrink-0 text-gray-200'>Provider</span>
            <span className='break-words text-left text-gray-200 min-[600px]:text-right'>{providerLabel}</span>
          </div>

          <div className='flex flex-col items-start gap-1 text-base leading-6 min-[600px]:flex-row min-[600px]:items-start min-[600px]:justify-between min-[600px]:gap-1.5'>
            <span className='shrink-0 text-brand-1100'>Rate</span>
            <div className='flex flex-wrap items-center justify-start gap-2 text-modal-result-body min-[600px]:justify-end'>
              <span className='break-words'>{rateLabel}</span>
              <RefreshIcon
                className={`h-4 w-4 shrink-0 text-modal-result-body ${isQuoteFetching || isLoadingQuote ? 'animate-spin' : ''}`}
              />
              <span className='inline-block w-10 shrink-0 text-right tabular-nums text-modal-result-body'>
                {secondsLeft} s
              </span>
            </div>
          </div>

          <div className='flex flex-col items-start gap-1 text-base leading-6 min-[600px]:flex-row min-[600px]:items-center min-[600px]:justify-between min-[600px]:gap-1.5'>
            <span className='shrink-0 text-brand-1100'>Expected Output</span>
            <span className='break-words text-left text-modal-result-body min-[600px]:text-right'>
              {expectedOutputLabel}
            </span>
          </div>

          <div className='flex flex-col items-start gap-1 text-base leading-6 min-[600px]:flex-row min-[600px]:items-center min-[600px]:justify-between min-[600px]:gap-1.5'>
            <span className='shrink-0 text-brand-1100'>Price Impact</span>
            <span className={cn('break-words text-left min-[600px]:text-right', priceImpactColor)}>
              {priceImpactLabel}
            </span>
          </div>

          <div className='flex flex-col items-start gap-1 text-base leading-6 min-[600px]:flex-row min-[600px]:items-center min-[600px]:justify-between min-[600px]:gap-1.5'>
            <span className='shrink-0 text-brand-1100'>Minimum received after slippage</span>
            <span className='break-words text-left text-modal-result-body min-[600px]:text-right'>
              {minReceivedLabel}
            </span>
          </div>
        </div>

        <div className='mt-auto flex flex-col gap-3'>
          <Button
            variant='outline'
            className={cn(
              'h-12 w-full py-0 text-base font-normal',
              isDangerButton &&
                'bg-feedback-error text-white enabled:hover:bg-feedback-error/90 enabled:active:bg-feedback-error disabled:bg-feedback-error disabled:text-white disabled:opacity-100',
            )}
            onClick={onActionClick}
            disabled={isActionDisabled}
          >
            {isRunning
              ? 'Processing...'
              : showQuoteError
                ? 'No quote available'
                : isPriceImpactUnavailable
                  ? 'Price impact unavailable'
                  : 'Swap'}
          </Button>

          {footerAlert ? (
            <SwapAlert message={footerAlert.message} variant={footerAlert.variant} compact />
          ) : null}
        </div>
      </div>

      <Suspense fallback={null}>
        <DepositStepperModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
          vaultName={vaultName?.trim() || 'Vault'}
          vaultId={vaultId}
          heading={`Deposit to ${trancheLabel}`}
          stages={stepperStages}
          alertMessage={stepperAlert.message}
          alertVariant={stepperAlert.variant}
          isSuccess={isStepperSuccess}
          primaryLabel={primaryAction.label}
          primaryDisabled={
            isStepperSuccess ? false : !isSequentialReady || (isRunning && !errorMessage)
          }
          primaryLoading={!isStepperSuccess && isRunning && !errorMessage}
          onPrimaryClick={
            isStepperSuccess || isSequentialReady ? handleStepperPrimaryClick : undefined
          }
        />
      </Suspense>
    </>
  );
};

export default DepositSwapDetailsForm;
